import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectBox } from 'cbc-shared-lib';
import {
  CodeConversionMstDto, Sgke401aResponseResponseCndtAuthorityList,
  Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo
} from 'sg-api';
import { ListSearchUtils } from '../../../../../sg-ke-shared/util/list-search-utils';

@Component({
  selector: 'app-sgke410d-condition',
  templateUrl: './sgke410d-condition.component.html',
  styleUrl: './sgke410d-condition.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke410dConditionComponent implements OnInit, OnDestroy {

  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報 */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;
  /** 検索条件権限リスト */
  @Input() cndtAuthorityList: Array<Sgke401aResponseResponseCndtAuthorityList>;
  /** コードマスタ取得 */
  @Input() codeConversionMstMap: Map<string, Array<CodeConversionMstDto>> = null;

  /** クリアボタン押下イベント */
  @Output() onClickClearEvent = new EventEmitter();
  /** 検索ボタン押下イベント */
  @Output() onClickSearchEvent = new EventEmitter();

  /** 会社コードリスト(S900020) */
  companycodeList: SelectBox[] = [];
  /** 地区コードリスト(S900030) */
  areaCodeList: SelectBox[] = [];
  /** 初期表示時の地区コードリスト(S900030) ※会社未選択時に全件表示するために保持 */
  initAreacodeList: SelectBox[] = [];
  /** 料金区分コードリスト(S300620) */
  pressmoneyDivcdList: SelectBox[] = [];

  /** 検索条件フォーム */
  searchConditionForm: UntypedFormGroup;
  /** 親FormGroup */
  parentForm: UntypedFormGroup;

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {UntypedFormBuilder} fb
   * @param {ChangeDetectorRef} cd
   * @param {ListSearchUtils} listSearchUtils
   * @memberof Sgke410dConditionComponent
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private listSearchUtils: ListSearchUtils,
  ) {
  }

  /**
   * 初期処理
   * @memberof Sgke410dConditionComponent
   */
  ngOnInit(): void {

    // セレクターに値をセットする
    this.companycodeList = this.getSelector('companycode', 'companycodeAname');
    this.areaCodeList = this.getSelector('areacode', 'areacodeAname');
    this.initAreacodeList = this.getSelector('areacode', 'areacodeAname');

    /** FormGroup */
    this.searchConditionForm = this.fb.group({
      companycode: this.fb.control({ value: null, disabled: false }),
      pressTantoAreacode: this.fb.control({ value: null, disabled: false }),
      pressmoneyDivcd: this.fb.control({ value: null, disabled: false }),
    });

    // FormGroupの登録
    this.parentForm = this.parent.form;
    // 親コンポーネントのformに検索条件formを追加
    this.parentForm.addControl("cndt", this.searchConditionForm);

    // "料金区分コード"リストを、SGKE401Aのレスポンスに存在する選択肢のみに絞り込む
    this.pressmoneyDivcdList = this.filterPressmoneyDivcdList();

    this.cd.detectChanges();

  }

  /**
   * @Inputプロパティが変更されたときに、その変更に関する情報を提供します。
   * @param {SimpleChanges} changes
   * @memberof Sgke410dConditionComponent
   */
  ngOnChanges(changes: SimpleChanges) {

    // 対象プロパティが変更された場合のみ処理する
    if (changes.codeConversionMstMap && this.codeConversionMstMap) {
      // コード値毎のkeyに対して、userCode(セレクトボックスの値)、hyojiCont(セレクトボックスの表示文字)がサービスから設定されているので、それを取得する
      this.pressmoneyDivcdList.push(...this.getSelectBoxFromCodeConversionMst('S300620'));
    }
  }

  /**
   * 終了処理
   *
   * @memberof Sgke410dConditionComponent
   */
  ngOnDestroy(): void {
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('cndt');
  }

  /**
   * SGKE903Aレスポンスからセレクターにオプションをセット
   * @param {string} value
   * @param {string} viewValue
   * @return {*} 
   * @memberof Sgke410dConditionComponent
   */
  getSelector(value: string, viewValue: string) {

    // SGKE903Aレスポンスのコードとコード値をまとめてリスト化
    let retSelectBoxList = this.cmpnyAreaList
      .map(data => {
        return { value: data[value], viewValue: data[viewValue] };
      })
      .reduce<SelectBox[]>((result, curr) => {
        const select = { value: curr[value], viewValue: curr[viewValue] }
        // 既に抽出済みでないものを追加
        if (!result.some((row) => row.value == curr.value)) {
          result.push(curr);
        }
        return result;
      }, [] as SelectBox[]);

    return retSelectBoxList;

  }

  /**
   * 料金区分コードセレクトリスト絞り込み
   * @return {*}
   * @memberof Sgke410dConditionComponent
   */
  filterPressmoneyDivcdList() {
    // SGKE401Aレスポンスから"料金区分コード(pressChargeClscode)"リストを抽出
    const pressChargeClscodeList = this.cndtAuthorityList.map((row) => row.chargeDivcd);
    // コード変換マスタの全"料金区分コード"のうち、SGKE401Aのレスポンスに存在する要素のみに絞り込む
    return this.pressmoneyDivcdList
      .filter((item) => {
        return pressChargeClscodeList.some(chargeClscode => chargeClscode === item.value)
      })
  }

  /**
   * UntypedFormControl取得
   * @param {*} ctrlName
   * @return {*}  {UntypedFormControl}
   * @memberof Sgke410dConditionComponent
   */
  getControl(ctrlName): UntypedFormControl {
    return this.searchConditionForm.get(ctrlName) as UntypedFormControl;
  }

  /**
   * クリアボタン押下時処理
   * @memberof Sgke410dConditionComponent
   */
  onClickClear(): void {
    this.searchConditionForm.reset(null);
    // 地区の全選択肢を設定するために実行
    this.onChangeCompany(null);
    this.cd.detectChanges();
    this.onClickClearEvent.emit();
  }

  /**
   * 会社ドロップダウン変化時処理
   * @param {string} companycode
   * @param {boolean} [formClearFlg=true]
   * @memberof Sgke410dConditionComponent
   */
  onChangeCompany(companycode: string, formClearFlg: boolean = true) {
    // 地区コードのフォームコントロール取得
    const chikuCtl = this.getControl('pressTantoAreacode') as UntypedFormControl;
    // 地区コードのフォームコントロールの中身をすべてnullにする
    formClearFlg ? chikuCtl?.setValue(null) : null;

    // 会社が選択された場合
    if (companycode !== '' && companycode !== null) {
      // 選択した会社コードを含む地区のみに絞り、地区コードリストに設定する
      this.areaCodeList = this.cmpnyAreaList
        .filter(list => list.companycode == companycode)
        .map(data => {
          const select: SelectBox = { value: data.areacode, viewValue: data.areacodeAname };
          return (select);
        });
      // 会社が未選択状態の場合
    } else {
      // すべての地区を、地区コードリストに設定する
      this.areaCodeList = this.initAreacodeList;
    }
  }

  /**
   * コードIDに対応するコード変換マスタ値をSelectBoxの配列で戻す
   * @param {string} codeId
   * @return {*}  {SelectBox[]}
   * @memberof Sgke410dConditionComponent
   */
  getSelectBoxFromCodeConversionMst(codeId: string): SelectBox[] {
    let retSelectBoxList: SelectBox[] = [];
    let codeList: CodeConversionMstDto[] = [];
    if (this.codeConversionMstMap.has(codeId)) {
      codeList = this.codeConversionMstMap.get(codeId);
      if (codeList) {
        codeList.forEach(data => {
          const select: SelectBox = { value: data.userCode, viewValue: data.hyojiCont };
          retSelectBoxList.push(select);
        })
      }
    }
    return retSelectBoxList;
  }
}
